/**
 * The queue as an inbox: the request list beside the one open request, copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/support/inbox/index.tsx (the 4/8 split) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/support/inbox/message-list.tsx (the list rows,
 * the active row's primary edge). The open request is a profile card — the gradient band and
 * avatar of vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx — over the
 * registration details, then the decision: AssignTermsForm, and DeclineTradeTermsForm behind its
 * own button so declining is a deliberate second step, never the form beside Grant.
 *
 * Only the data and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState, type ReactNode } from "react";
import toast from "react-hot-toast";
import { Link, useLoaderData, useOutletContext, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import {
  PiAddressBookBold as ContactIcon,
  PiArrowSquareOutBold as OpenIcon,
  PiBuildingsBold as BusinessIcon,
  PiCalendarBlankBold as CalendarIcon,
  PiMagnifyingGlassBold as SearchIcon,
  PiMapPinBold as AddressIcon,
  PiSealCheckBold as GrantIcon,
  PiWalletBold as AccountIcon,
} from "react-icons/pi";
import { Alert, Avatar, Button, Input, Text, Title } from "rizzui";
import { EmptyState, ErrorSurface, cn, getStatusBadge } from "@steelyard/theme";
import { can } from "../../lib/permissions";
import type { PermissionState } from "../../types";
import { AssignTermsForm, type TierOption } from "../../components/customers/assign-terms-form";
import { DeclineTradeTermsForm, parseTradeTermsQueue, type TradeTermsRequest } from "../../components/customers/customer-trade-terms-queue";
import { CardTitle, CopyButton, StatusPill } from "../../components/detail-parts";
import { ListHeader } from "../../components/table/list-header";
import { listActiveTiers } from "../../lib/services/tier-options";
import { addressTypeLabel, assignTerms, declineTradeTerms, getCustomer, getTradeTermsQueue, type CustomerDetail } from "../../lib/services/customers";
import { formatDate, formatMoment, formatMoney } from "../../lib/format";
import { customerPath } from "../../lib/paths";

/**
 * Registration details and requested terms on one screen (FR-009), for the request in `?request=`
 * (the first one when absent or already decided). One customer read per view rather than a fan-out
 * over the queue — `GET /admin/trade-terms` takes no paging, so the list itself is the whole queue.
 */
export async function tradeTermsLoader({ request }: LoaderFunctionArgs) {
  const wanted = new URL(request.url).searchParams.get("request");
  const queue = parseTradeTermsQueue(await getTradeTermsQueue());
  const selected = queue.find((entry) => entry.requestId === wanted) ?? queue[0];

  const [tiers, read] = await Promise.all([
    listActiveTiers(),
    // A value that isn't a customer is the read's own failure, kept so the card can show why.
    selected ? getCustomer(selected.customerId).catch((error: unknown) => error) : undefined,
  ]);

  return { queue, selected, tiers, read };
}

export default function CustomersTradeTerms() {
  const { queue, selected, tiers, read } = useLoaderData() as Awaited<ReturnType<typeof tradeTermsLoader>>;
  const revalidator = useRevalidator();
  const { permissionState } = useOutletContext<{ permissionState: PermissionState }>();
  // Declining is `customers.administer` (CustomerAdminController); granting is `customers.terms`.
  const mayDecline = can(permissionState, "customers.administer");
  const [declined, setDeclined] = useState(false);

  async function handleDecline(requestId: string, formData: FormData) {
    try {
      await declineTradeTerms(requestId, formData);
      setDeclined(true);
      toast.success("Request declined");
      revalidator.revalidate();
    } catch (err) {
      const problem = err as { detail?: string; title?: string };
      toast.error(problem.detail ?? problem.title ?? "Could not decline the request");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Trade-Terms Queue"
        count={queue.length}
        description="Customers asking for a tier, a credit limit and a payment term. Granting them converts the customer to B2B."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Customers", href: "/customers" }, { name: "Trade-Terms Queue" }]}
      />

      {declined ? (
        <div role="status">
          <Alert color="success" bar>
            Declined, and the reason has been recorded and notified. The customer keeps the tier they already have and
            can keep ordering on it.
          </Alert>
        </div>
      ) : null}

      {selected ? (
        <div className="@container">
          <div className="grid items-start gap-6 @4xl:grid-cols-12">
            <RequestList queue={queue} selectedId={selected.requestId} className="@4xl:sticky @4xl:top-24 @4xl:col-span-4" />
            <RequestDetail
              key={selected.requestId}
              className="@4xl:col-span-8"
              request={selected}
              read={read}
              tiers={tiers}
              revalidator={revalidator}
              onDecline={mayDecline ? handleDecline : undefined}
            />
          </div>
        </div>
      ) : (
        <EmptyState title="Nothing waiting">No customer has an open trade-terms request.</EmptyState>
      )}
    </div>
  );
}

function RequestList({ queue, selectedId, className }: { queue: readonly TradeTermsRequest[]; selectedId: string; className?: string }) {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  // The whole queue is loaded (no paging on the route), so filtering it here searches everything.
  const shown = queue.filter((entry) => (entry.displayName ?? "").toLowerCase().includes(search.trim().toLowerCase()));

  function hrefFor(requestId: string) {
    const params = new URLSearchParams(searchParams);
    params.set("request", requestId);
    return `?${params.toString()}`;
  }

  return (
    <section aria-label="Waiting for a decision" className={cn("overflow-hidden rounded-xl border border-muted bg-gray-0 dark:bg-gray-50", className)}>
      <div className="flex flex-col gap-3 border-b border-muted px-5 py-4">
        <Title as="h3" className="text-sm font-semibold">Waiting for a decision</Title>
        <Input
          aria-label="Search requests by customer"
          placeholder="Search customers…"
          size="sm"
          prefix={<SearchIcon aria-hidden className="size-4 text-gray-500" />}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          clearable
          onClear={() => setSearch("")}
        />
      </div>

      {shown.length === 0 ? (
        <Text className="px-5 py-6 text-sm text-gray-500">No request matches “{search}”.</Text>
      ) : (
        <ul className="max-h-[calc(100vh-18rem)] overflow-y-auto">
          {shown.map((entry) => {
            const active = entry.requestId === selectedId;
            const name = entry.displayName ?? "Unknown customer";
            return (
              <li key={entry.requestId} className="border-b border-muted last:border-b-0">
                <Link
                  to={hrefFor(entry.requestId)}
                  aria-current={active ? "true" : undefined}
                  className={cn(
                    "flex items-center gap-3 border-s-2 px-5 py-3.5 transition-colors",
                    active ? "border-primary bg-primary-lighter/40 dark:bg-gray-100/70" : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-100/50",
                  )}
                >
                  <Avatar name={name} size="sm" color={active ? "primary" : "secondary"} className="shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className={cn("block truncate text-sm", active ? "font-semibold text-gray-900" : "font-medium text-gray-800")}>{name}</span>
                    <span className="mt-0.5 block text-xs text-gray-500">Requested {formatDate(entry.requestedAt)}</span>
                  </span>
                  {entry.status ? <span className="shrink-0">{getStatusBadge(entry.status)}</span> : null}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function RequestDetail({
  request,
  read,
  tiers,
  revalidator,
  onDecline,
  className,
}: {
  request: TradeTermsRequest;
  read: unknown;
  tiers: readonly TierOption[];
  revalidator: ReturnType<typeof useRevalidator>;
  onDecline?: (requestId: string, formData: FormData) => void | Promise<void>;
  className?: string;
}) {
  const customer = read instanceof Error || read === undefined ? undefined : (read as CustomerDetail);
  const readError = (read ?? {}) as { status?: number; title?: string; detail?: string };
  const name = customer?.summary.displayName ?? request.displayName ?? "Unknown customer";
  const outstanding = customer ? formatMoney(Number(customer.summary.outstanding)) : undefined;
  const [error, setError] = useState<unknown>(null);
  const [declining, setDeclining] = useState(false);

  async function handleAssign(formData: FormData) {
    setError(null);
    try {
      await assignTerms(request.customerId, formData);
      toast.success("Terms assigned");
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      const problem = err as { detail?: string; title?: string };
      toast.error(problem.detail ?? problem.title ?? "Could not assign terms");
    }
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-6", className)}>
      <section aria-label="Registration details" className="@container overflow-hidden rounded-xl border border-muted bg-gray-0 dark:bg-gray-50">
        <div className="flex flex-col gap-4 bg-gradient-to-r from-primary/10 via-secondary/5 to-transparent px-6 py-5 @2xl:flex-row @2xl:items-center @2xl:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar name={name} size="lg" color="primary" className="shrink-0 shadow-sm" />
            <div className="min-w-0">
              <Title as="h2" className="truncate text-lg font-semibold">{name}</Title>
              {customer && customer.legalName !== name ? <Text className="truncate text-sm text-gray-600">{customer.legalName}</Text> : null}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                {request.status ? <StatusPill status={request.status} /> : null}
                <span className="inline-flex items-center gap-1.5">
                  <CalendarIcon aria-hidden className="size-3.5" />
                  Requested {formatMoment(request.requestedAt)}
                </span>
              </div>
            </div>
          </div>
          <Link to={customerPath(request.customerNumber || request.customerId)} className="shrink-0">
            <Button as="span" variant="outline" size="sm" className="whitespace-nowrap bg-gray-0 dark:bg-gray-50">
              Open customer record
              <OpenIcon aria-hidden className="ms-1.5 size-3.5" />
            </Button>
          </Link>
        </div>

        <div className="border-t border-muted px-6 py-6">
          {customer ? (
            <div className="grid gap-x-8 gap-y-7 @2xl:grid-cols-2 @5xl:grid-cols-3">
              <FactGroup icon={<BusinessIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" title="Business">
                <Fact label="Legal name">{customer.legalName}</Fact>
                <Fact label="GSTIN" mono copy={customer.gstin}>{customer.gstin}</Fact>
                <Fact label="PAN" mono>{customer.pan}</Fact>
              </FactGroup>
              {/* §10.3 step 3: the registrant is the firm's first user (RegisterCustomerHandler); an individual has none. */}
              <FactGroup icon={<ContactIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" title="Primary contact">
                <Fact label="Name">{customer.users[0]?.name ?? customer.legalName}</Fact>
                <Fact label="Mobile">{customer.registeredMobile}</Fact>
                <Fact label="Email" copy={customer.email}>{customer.email}</Fact>
              </FactGroup>
              <FactGroup icon={<AccountIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" title="Account">
                <Fact label="Outstanding">
                  <span className="text-base font-semibold">{outstanding}</span>
                </Fact>
                {customer.addresses.map((address, index) => (
                  <Fact key={`${address.type}-${index}`} label={addressTypeLabel(address.type) === "billing" ? "Billing address" : "Shipping address"} icon={<AddressIcon aria-hidden className="size-3.5" />}>
                    {[address.line1, address.city, address.pincode].filter(Boolean).join(", ")}
                  </Fact>
                ))}
              </FactGroup>
            </div>
          ) : (
            <ErrorSurface
              error={{
                title: readError.title ?? "Could not read this customer's registration details",
                status: readError.status,
                detail:
                  (readError.detail ? `${readError.detail} ` : "") +
                  "The request is still here and can still be decided, but the details that inform the decision are missing. Open the customer record before granting terms.",
              }}
              action="show the registration details"
            />
          )}
        </div>
      </section>

      <section aria-label="Decision" className="overflow-hidden rounded-xl border border-muted bg-gray-0 dark:bg-gray-50">
        <div className="border-b border-muted px-6 py-4">
          <CardTitle icon={<GrantIcon aria-hidden className="size-5" />} tile="bg-primary-lighter text-primary-dark">
            <span className="flex flex-col">
              <Title as="h3" className="text-base font-semibold">Decision</Title>
              <Text className="text-sm font-normal text-gray-500">Grant a tier, credit limit and payment term — or decline with a reason.</Text>
            </span>
          </CardTitle>
        </div>

        <div className="px-6 py-6">
          <AssignTermsForm
            onSubmit={handleAssign}
            tiers={tiers}
            customerName={name}
            tradeTermsRequestId={request.requestId}
            currentOutstanding={outstanding}
            error={error}
          />
        </div>

        {onDecline ? (
          <div className="border-t border-dashed border-muted bg-gray-50/60 px-6 py-5 dark:bg-gray-100/30">
            {declining ? (
              <div className="flex flex-col gap-3">
                <Title as="h4" className="text-sm font-semibold">Decline this request</Title>
                <DeclineTradeTermsForm onSubmit={(formData) => onDecline(request.requestId, formData)} customerName={name} requestId={request.requestId} />
                <Button variant="text" size="sm" className="self-start px-0 text-gray-600" onClick={() => setDeclining(false)}>
                  Keep the request open
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <Text className="text-sm font-semibold text-gray-900">Not granting terms?</Text>
                  <Text className="text-sm text-gray-500">{name} stays on the Default Tier at piece prices. The reason is sent to them.</Text>
                </div>
                <Button variant="outline" color="danger" size="sm" className="shrink-0" onClick={() => setDeclining(true)}>
                  Decline…
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function FactGroup({ icon, tile, title, children }: { icon: ReactNode; tile: string; title: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex items-center gap-2.5">
        <span className={cn("flex size-8 shrink-0 items-center justify-center rounded-lg", tile)}>{icon}</span>
        <Title as="h3" className="text-sm font-semibold">{title}</Title>
      </div>
      <dl className="flex flex-col gap-3.5 border-s border-muted ps-[1.3rem]">{children}</dl>
    </div>
  );
}

function Fact({ label, mono, copy, icon, children }: { label: string; mono?: boolean; copy?: string | null; icon?: ReactNode; children: ReactNode }) {
  const missing = children === null || children === undefined || children === "";
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {icon}
        {label}
      </dt>
      <dd className="mt-1 flex items-center gap-1">
        {missing ? (
          <span className="text-sm italic text-gray-500">Not given</span>
        ) : (
          <span className={cn("break-words text-sm text-gray-900", mono && "font-mono tracking-wide")}>{children}</span>
        )}
        {copy ? <CopyButton value={copy} label={`Copy ${label.toLowerCase()}`} /> : null}
      </dd>
    </div>
  );
}
